import { apiGet, apiPost, apiPut } from '../../lib/api'
import { withAuth } from '../auth/token'
import { DOCUMENT_TYPES_BY_OWNER, INSURANCE_COVERAGES } from '../documents/api'

/**
 * Mirrors the alert, window and activation slices on the API.
 *
 * The five owner kinds travel as the server's own names, so a row can be turned into
 * a link without a translation table: every fleet screen lives under its carrier,
 * which is why an alert about a driver carries the carrier it belongs to.
 */
export const OWNER_TYPES = ['Broker', 'Carrier', 'Driver', 'Truck', 'Trailer'] as const

export type OwnerType = (typeof OWNER_TYPES)[number]

/** Past its last valid day, or not yet — the API's own names (TRKC-97). */
export const ALERT_SITUATIONS = ['Expired', 'Expiring'] as const

export type AlertSituation = (typeof ALERT_SITUATIONS)[number]

/**
 * Every paper the list can be narrowed to: each coverage line and every type a record can
 * file — the retired one-date certificate included, whose rows still alert. A mirror of the
 * rulebook, like the documents panel's; a type the server does not know finds nothing.
 */
export const ALERT_PAPERS: readonly string[] = [
  ...new Set([
    ...INSURANCE_COVERAGES.map((coverage) => coverage.type),
    ...Object.values(DOCUMENT_TYPES_BY_OWNER).flatMap((papers) => papers.map((paper) => paper.type)),
  ]),
]

/** What the list is narrowed to (TRKC-97). Every filter is optional. */
export interface AlertFilter {
  ownerType?: OwnerType
  documentType?: string
  situation?: AlertSituation
  /** One carrier's papers and its fleet's. */
  carrierId?: string
}

export interface ComplianceAlert {
  documentId: string
  ownerType: OwnerType
  ownerId: string
  ownerName: string
  /** Null for a broker or a carrier; where a fleet child's screen lives otherwise. */
  carrierId: string | null
  documentType: string
  expiresOn: string
  /** The narrowest window this paper crossed — zero once it has lapsed. */
  thresholdDays: number
  /** Negative once the paper is past its last valid day. */
  daysRemaining: number
  isExpired: boolean
}

export interface AlertWindow {
  documentType: string
  warningDays: number[]
  critical: boolean
  requiredOwners: OwnerType[]
}

export interface EvaluationSummary {
  ownersEvaluated: number
  alertsRaised: number
  alertsResolved: number
  /** Owners skipped because somebody edited them mid-run — reported, never swallowed. */
  conflicts: number
}

/**
 * What a change to the windows did: the windows as the server now holds them, and
 * what the re-judgement they triggered got through — `evaluation.conflicts` counts the
 * records that were being edited mid-run and kept their alerts under the old windows.
 */
export interface AlertWindowUpdate {
  window: AlertWindow
  evaluation: EvaluationSummary
}

export interface Activation {
  ownerType: OwnerType
  ownerId: string
  active: boolean
  deactivatedAt: string | null
  /**
   * Whether the compliance standing was actually re-judged after the change. False
   * when the record moved underneath the evaluation — the activation itself is saved
   * either way, so this is a staleness warning, never a failed save.
   */
  standingConfirmed: boolean
}

export function listAlerts(filter: AlertFilter = {}): Promise<ComplianceAlert[]> {
  const query = new URLSearchParams()

  for (const [key, value] of Object.entries(filter)) {
    if (value !== undefined) {
      query.set(key, value)
    }
  }

  const text = query.toString()
  return apiGet<ComplianceAlert[]>(`/api/alerts${text === '' ? '' : `?${text}`}`, { headers: withAuth() })
}

export function runEvaluation(): Promise<EvaluationSummary> {
  return apiPost<EvaluationSummary>('/api/alerts/run', undefined, { headers: withAuth() })
}

export function listAlertWindows(): Promise<AlertWindow[]> {
  return apiGet<AlertWindow[]>('/api/alert-windows', { headers: withAuth() })
}

export function updateAlertWindows(
  documentType: string,
  warningDays: number[],
): Promise<AlertWindowUpdate> {
  return apiPut<AlertWindowUpdate>(
    `/api/alert-windows/${documentType}`,
    { warningDays },
    { headers: withAuth() },
  )
}

export function setActivation(
  ownerType: OwnerType,
  ownerId: string,
  active: boolean,
): Promise<Activation> {
  return apiPut<Activation>(
    `/api/activation/${ownerType}/${ownerId}`,
    { active },
    { headers: withAuth() },
  )
}
